import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertCircle,
  AlertTriangle,
  ArrowUpRight,
  Ban,
  Banknote,
  BedDouble,
  Calendar,
  CheckCircle2,
  ClipboardList,
  FileText,
  Loader2,
  Phone,
  Printer,
  Receipt,
  ShieldAlert,
  Stethoscope,
  UserPlus,
  UserRound,
  Wallet,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  useAdmissionBillingStatus,
  useBulkRecordPayments,
  useMoveToRiskFund,
} from '../../services/httpServices/queries';
import { ipdService } from '../../services/httpServices/ipdService';
import { printInvoicePdf } from '../../utils/printDocument';
import type { CreatePaymentDto } from '../../types/billing';
import { useAppSelector } from '../../redux/store/hooks';
import { usePermissions } from '../../hooks/usePermissions';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { RiskFundMoveDialog } from '../../components/ui/RiskFundMoveDialog';
import { AdmissionPaymentBadge } from '../../components/ui/admission-payment-badge';
import {
  isRiskFundInvoiceStatus,
  paymentStateFromBilling,
  resolveAdmissionPaymentState,
} from '../../utils/admissionPayment';
import { isCancelledAdmission, isEmergencyAdmission } from '../../utils/admission';
import { joinFullName, patientInitials } from '../../utils/patientName';
import { formatOrdinalDateTimeLong } from '../../utils/dateFormat';
import { useUpdateAdmission } from '../../services/httpServices/queries/useIpd';
import { useHospitalTimezone } from '../../hooks/useHospitalTimezone';
import { DateTimePicker } from '../../components/ui/DatePicker';
import { InfoHint } from '../../components/ui/InfoHint';
import { datetimeLocalToISOInTimeZone, toDatetimeLocalValueInTimeZone } from '../../lib/timezones';

// Must match the backend PaymentMethod enum (lowercase).
const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
];

const money = (value: number) =>
  `৳${(Number.isFinite(value) ? value : 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}`;

const REFERRAL_TYPE_LABEL: Record<string, string> = {
  DOCTOR: 'Doctor',
  EMPLOYEE: 'Employee',
  OTHER: 'Outside partner',
};

/** "DISCHARGE_INITIATED" -> "Discharge Initiated"; empty -> "—". */
const titleCase = (value?: string | null) =>
  value
    ? value
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : '—';

/**
 * Decorative pill used in the header for a compact piece of patient context
 * (registration number, age/sex, phone). Kept translucent so it reads well on
 * top of the tinted hero gradient in both light and dark themes.
 */
function MetaChip({
  icon: Icon,
  children,
}: {
  icon?: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-surface/80 px-2 py-0.5 text-[10px] font-medium text-text-muted backdrop-blur">
      {Icon && <Icon size={10} className="shrink-0" />}
      {children}
    </span>
  );
}

/** One labelled fact in the details grid. */
function InfoTile({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-border bg-surface px-3 py-2.5 transition-colors hover:border-primary/30">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon size={15} />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
          {label}
        </p>
        <p className="truncate text-xs font-semibold text-text" title={value}>
          {value}
        </p>
        {hint && (
          <p className="truncate text-[10px] text-text-muted" title={hint}>
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}

/** Tinted amount tile for the billing summary row. */
function AmountTile({
  label,
  value,
  tone,
  tint,
}: {
  label: string;
  value: number;
  tone: string;
  tint: string;
}) {
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${tint}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
        {label}
      </p>
      <p
        className={`mt-0.5 truncate text-base font-bold leading-tight ${tone}`}
        title={money(value)}
      >
        {money(value)}
      </p>
    </div>
  );
}

/** A titled block for free-text clinical notes (diagnosis, summary, ...). */
function NoteCard({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-surface px-3.5 py-3">
      <div className="mb-1 flex items-center gap-1.5">
        <Icon size={12} className="text-primary" />
        <p className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
          {title}
        </p>
      </div>
      {children}
    </div>
  );
}

interface IpdAdmissionDetailsModalProps {
  /** The discharged admission to summarise, or null when the modal is closed. */
  admission: any | null;
  onClose: () => void;
  onViewAdmission: (admissionId: string) => void;
  /** Whether the current user may record invoice payments. */
  canPay: boolean;
}

export function IpdAdmissionDetailsModal({
  admission,
  onClose,
  onViewAdmission,
  canPay,
}: IpdAdmissionDetailsModalProps) {
  const { user } = useAppSelector((state) => state.auth);
  const queryClient = useQueryClient();
  const bulkRecordPayments = useBulkRecordPayments();
  const moveToRiskFund = useMoveToRiskFund();
  const { hasPermission } = usePermissions();
  // Sensitive finance action: admins and custom roles granted the capability.
  const canManageRiskFund = hasPermission('billing:risk-fund:manage');
  // Correcting a stay's admission time (backdating) is its own per-role
  // capability — the API refuses `admittedAt` without it. The list is the main
  // working screen for IPD, so the correction belongs here as well as on the
  // admission page.
  const canEditAdmissionTime = hasPermission('ipd:admission:backdate');
  const updateAdmission = useUpdateAdmission();

  // Hospital timezone configured on Settings → General (`hospital_timezone`),
  // matching the admission form and the receipt screens. Read from the public
  // settings endpoint so every role resolves the hospital's calendar.
  const { timezone: hospitalTimezone } = useHospitalTimezone();

  // The list row only carries aggregate amounts. Pull the live billing state so
  // we know which invoices still carry a balance and can settle them together.
  const { data: billingRes, isLoading: billingLoading } = useAdmissionBillingStatus(
    admission?.id,
    { enabled: !!admission?.id },
  );
  const billing = billingRes?.data;

  const [payOpen, setPayOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [amount, setAmount] = useState(0);
  const [amountDirty, setAmountDirty] = useState(false);
  const [mode, setMode] = useState('cash');
  const [reference, setReference] = useState('');
  // When the money was actually received. Recorded as the payment date so a
  // carried due lands in "collected" on the day it was paid, not the bill date.
  // Seeded in the hospital's zone once the settings land (below).
  const [paymentDate, setPaymentDate] = useState(() => toDatetimeLocalValueInTimeZone(new Date()));
  const [error, setError] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [riskFundOpen, setRiskFundOpen] = useState(false);
  const [riskFundReason, setRiskFundReason] = useState('');
  const [riskFundAmount, setRiskFundAmount] = useState('');
  const [riskFundError, setRiskFundError] = useState<string | null>(null);

  // ── Admission date (backdate) ──
  // The list passes the row as a snapshot, so the stored instant is mirrored in
  // local state: saving the new date has to move the header, the stay length and
  // the "unsaved" state in this same dialog.
  const [storedAdmittedAt, setStoredAdmittedAt] = useState<string | null>(null);
  const [admittedAtValue, setAdmittedAtValue] = useState('');
  const [admissionDateError, setAdmissionDateError] = useState<string | null>(null);
  const admittedAtSeededRef = useRef(false);

  const invoices = useMemo<any[]>(
    () => (Array.isArray(billing?.invoices) ? billing!.invoices : []),
    [billing],
  );
  const dueInvoices = useMemo(
    () => invoices.filter((inv) => Number(inv.dueAmount || 0) > 0),
    [invoices],
  );
  const selectedInvoices = useMemo(
    () => dueInvoices.filter((inv) => selectedIds.includes(inv.id)),
    [dueInvoices, selectedIds],
  );
  const selectedDue = useMemo(
    () =>
      Number(
        selectedInvoices
          .reduce((sum, inv) => sum + (Number(inv.dueAmount) || 0), 0)
          .toFixed(2),
      ),
    [selectedInvoices],
  );
  const allSelected =
    dueInvoices.length > 0 && selectedInvoices.length === dueInvoices.length;
  const remainingDue = Math.max(
    0,
    Number((selectedDue - (Number(amount) || 0)).toFixed(2)),
  );

  // Keep the amount pinned to the total due of the selected invoices while the
  // user hasn't typed their own amount, so ticking invoices updates it.
  useEffect(() => {
    if (!payOpen || amountDirty) return;
    setAmount(Number(selectedDue.toFixed(2)));
  }, [payOpen, amountDirty, selectedDue]);

  /**
   * The stored admission time, rendered as the picker's hospital wall clock.
   * Changing it (backdating the stay) also re-dates the visit's live invoices
   * and their accrual ledger entries on the server.
   */
  const savedAdmittedAtLocal = useMemo(() => {
    if (!storedAdmittedAt) return '';
    const instant = new Date(storedAdmittedAt);
    if (Number.isNaN(instant.getTime())) return '';
    return toDatetimeLocalValueInTimeZone(instant, hospitalTimezone);
  }, [storedAdmittedAt, hospitalTimezone]);

  // Follow the row being opened; a different admission re-seeds the picker.
  useEffect(() => {
    if (!admission) return;
    setStoredAdmittedAt(admission.admittedAt || admission.admissionDate || null);
    admittedAtSeededRef.current = false;
    setAdmissionDateError(null);
  }, [admission]);

  // Seed the picker once per row (and again if the hospital zone arrives late),
  // never over an edit the counter is in the middle of making.
  useEffect(() => {
    if (!admission || admittedAtSeededRef.current) return;
    admittedAtSeededRef.current = true;
    setAdmittedAtValue(
      storedAdmittedAt
        ? savedAdmittedAtLocal
        : toDatetimeLocalValueInTimeZone(new Date(), hospitalTimezone),
    );
  }, [admission, storedAdmittedAt, savedAdmittedAtLocal, hospitalTimezone]);

  const admissionDateChanged =
    canEditAdmissionTime &&
    Boolean(admittedAtValue) &&
    admittedAtValue !== savedAdmittedAtLocal;

  const handleSaveAdmissionDate = async () => {
    if (!admission?.id) return;
    if (!admittedAtValue) {
      setAdmissionDateError('Admission date & time is required.');
      return;
    }
    setAdmissionDateError(null);
    const iso = datetimeLocalToISOInTimeZone(admittedAtValue, hospitalTimezone);
    try {
      await updateAdmission.mutateAsync({ id: admission.id, data: { admittedAt: iso } });
      // Mirror the saved value locally: the dialog's header, stay length and
      // "unsaved" flag all read it, and the list row it was opened from is a
      // snapshot that only refreshes after the invalidation round-trip.
      setStoredAdmittedAt(iso);
      toast.success('Admission date updated — invoices re-dated');
    } catch (err: any) {
      setAdmissionDateError(err?.message || 'Failed to update the admission date.');
    }
  };

  // Escape closes the topmost layer and the overlay locks background scroll,
  // matching the rest of the app's dialogs.
  useEffect(() => {
    if (!admission) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (riskFundOpen) {
        if (!moveToRiskFund.isPending) setRiskFundOpen(false);
        return;
      }
      if (payOpen) {
        if (!bulkRecordPayments.isPending) setPayOpen(false);
        return;
      }
      if (!printing) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [admission, payOpen, printing, riskFundOpen, moveToRiskFund.isPending, bulkRecordPayments.isPending, onClose]);

  if (!admission) return null;

  const patient = admission.patient || {};
  const patientName = joinFullName(patient) || patient.registrationNumber || 'Unknown';
  const initials = patientInitials(patient);
  const bed = admission.bed;
  const isEmergency = isEmergencyAdmission(admission);
  const bedLabel = bed
    ? `${bed.bedNumber || 'Bed'} · ${bed.wardEntity?.wardName || bed.ward || 'Ward'}`
    : 'Emergency (no bed)';
  const diagnosis = admission.diagnosis || admission.admissionReason || 'N/A';
  const admittedAt = storedAdmittedAt || admission.admittedAt || admission.admissionDate;
  const dischargedAt = admission.dischargedAt;
  // A cancelled admission never reaches a discharge: it is closed early and its
  // bed is released. The modal is the only place staff can review that stay
  // (list click no longer opens the working admission page), so it must say
  // "Cancelled" instead of borrowing the discharge copy.
  const cancelled = isCancelledAdmission(admission);
  // Admissions have no `cancelledAt` column; the row's last update is when the
  // cancellation was written (the status is terminal for the stay).
  const cancelledAt: string | undefined = cancelled
    ? admission.cancelledAt || admission.updatedAt || undefined
    : undefined;
  const closedAt = cancelled ? cancelledAt : dischargedAt;
  const statusLabel = cancelled ? 'Cancelled' : titleCase(admission.status);

  // Two separate concepts:
  //  • Referral partner → payee of a flat commission once the bill settles.
  //    Only exists when a partner is linked; independent of `admissionType`.
  //  • Referred by → the doctor/clinic who sent the patient. Clinical
  //    attribution only; it never creates a commission or a payout.
  const referral = admission.referral || null;
  const hasReferralPartner = Boolean(admission.referralId || referral?.id);
  const referralFlatAmount = Number(admission.referralFlatAmount) || 0;
  const referralName = referral?.name || referral?.organization || null;
  const referralDetail = [
    referral?.designation,
    referral?.specialization,
    referral?.organization,
  ]
    .filter(Boolean)
    .join(' · ');
  const referredByName = admission.referredBy?.name || null;
  const referredFrom = admission.referralDetails || null;

  // Nights between admission and the stay's end (discharge or cancellation),
  // used for the stay timeline.
  const stayNights = (() => {
    if (!admittedAt || !closedAt) return null;
    const start = new Date(admittedAt).getTime();
    const end = new Date(closedAt).getTime();
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
    return Math.max(1, Math.round((end - start) / 86_400_000));
  })();

  // Prefer the live invoice figures; fall back to the list aggregates while the
  // billing status is still loading.
  const totals = (() => {
    if (invoices.length > 0) {
      return invoices.reduce(
        (acc, inv) => ({
          total: acc.total + (Number(inv.total) || 0),
          paid: acc.paid + (Number(inv.paidAmount) || 0),
          due: acc.due + (Number(inv.dueAmount) || 0),
        }),
        { total: 0, paid: 0, due: 0 },
      );
    }
    return {
      total: Number(admission.totalAmount) || 0,
      paid: Number(admission.paidAmount) || 0,
      due: billing ? Number(billing.outstandingDue) || 0 : Number(admission.dueAmount) || 0,
    };
  })();
  const paidPercent =
    totals.total > 0 ? Math.min(100, Math.round((totals.paid / totals.total) * 100)) : 0;

  // The hero used to read `due > 0 ? Due : Settled`, which mislabelled a Risk
  // Fund admission as settled: moving a balance to the Risk Fund clears the
  // due, but the money was written off — it was never paid. Resolve the real
  // state from the live invoices so Risk Fund is shown (and highlighted) as
  // its own state, distinct from Paid.
  const paymentState =
    paymentStateFromBilling({
      invoices,
      outstandingDue: billing?.outstandingDue,
      unbilledCount: billing?.unbilledCount,
      ignoreUnbilled: cancelled,
    }) ?? resolveAdmissionPaymentState(admission);

  const writtenOffAmount =
    paymentState === 'RISK_FUND'
      ? Math.max(0, Number((totals.total - totals.paid).toFixed(2)))
      : 0;

  const openPayment = () => {
    const ids = dueInvoices.map((inv) => inv.id);
    setSelectedIds(ids);
    setAmountDirty(false);
    setAmount(
      Number(
        dueInvoices
          .reduce((sum, inv) => sum + (Number(inv.dueAmount) || 0), 0)
          .toFixed(2),
      ),
    );
    setMode('cash');
    setReference('');
    setPaymentDate(toDatetimeLocalValueInTimeZone(new Date(), hospitalTimezone));
    setError(null);
    setPayOpen(true);
  };

  const closePayment = () => {
    if (bulkRecordPayments.isPending) return;
    setPayOpen(false);
    setError(null);
  };

  // Any change to the selection re-pins the amount to the new total due; the
  // user can still type a smaller partial amount afterwards.
  const toggleInvoice = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
    setAmountDirty(false);
    setError(null);
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : dueInvoices.map((inv) => inv.id));
    setAmountDirty(false);
    setError(null);
  };

  const handlePay = async () => {
    if (selectedInvoices.length === 0) {
      setError('Select at least one invoice to pay.');
      return;
    }
    const value = Number(amount) || 0;
    if (value <= 0) {
      setError('Enter an amount greater than zero.');
      return;
    }
    if (value > selectedDue + 0.001) {
      setError(`Amount cannot exceed the selected due of ${money(selectedDue)}.`);
      return;
    }
    setError(null);

    // Split the collected amount across the selected invoices, oldest first,
    // so one payment can settle several bills at once.
    const payments: CreatePaymentDto[] = [];
    let remaining = Number(Math.min(value, selectedDue).toFixed(2));
    for (const invoice of selectedInvoices) {
      if (remaining <= 0.004) break;
      const due = Number(invoice.dueAmount) || 0;
      const invoiceAmount = Number(Math.min(remaining, due).toFixed(2));
      if (invoiceAmount <= 0) continue;
      payments.push({
        invoiceId: invoice.id,
        amount: invoiceAmount,
        paymentMode: mode,
        referenceNumber: reference.trim() || undefined,
        paymentDate: paymentDate
          ? datetimeLocalToISOInTimeZone(paymentDate, hospitalTimezone)
          : undefined,
        receivedBy: user?.id,
      });
      remaining = Number((remaining - invoiceAmount).toFixed(2));
    }

    if (payments.length === 0) {
      setError('Nothing left to pay on the selected invoices.');
      return;
    }

    try {
      await bulkRecordPayments.mutateAsync(payments);
      const collected = payments.reduce((sum, payment) => sum + payment.amount, 0);
      const paidByInvoice = new Map(payments.map((payment) => [payment.invoiceId, payment.amount]));

      // Update the caches optimistically first so the modal and the list row
      // change the instant the payment lands — then invalidate to reconcile
      // with the server (which also removes the row when filtering by DUE).
      queryClient.setQueriesData(
        { queryKey: ['admission-billing-status', admission.id] },
        (old: any) => {
          const data = old?.data;
          if (!data) return old;
          const list = Array.isArray(data.invoices) ? data.invoices : [];
          const nextInvoices = list.map((invoice: any) => {
            const paid = paidByInvoice.get(invoice.id) || 0;
            if (paid <= 0) return invoice;
            return {
              ...invoice,
              paidAmount: Number(
                ((Number(invoice.paidAmount) || 0) + paid).toFixed(2),
              ),
              dueAmount: Number(
                Math.max(0, (Number(invoice.dueAmount) || 0) - paid).toFixed(2),
              ),
            };
          });
          const outstandingDue = Number(
            nextInvoices
              .reduce((sum: number, invoice: any) => sum + (Number(invoice.dueAmount) || 0), 0)
              .toFixed(2),
          );
          return {
            ...old,
            data: {
              ...data,
              invoices: nextInvoices,
              outstandingDue,
              unpaidInvoiceCount: nextInvoices.filter(
                (invoice: any) => Number(invoice.dueAmount) > 0,
              ).length,
              hasOutstandingDue: outstandingDue > 0,
              isCleared: outstandingDue <= 0 && Number(data.unbilledCount || 0) === 0,
            },
          };
        },
      );

      queryClient.setQueriesData(
        { queryKey: ['admissions', 'paginated'] },
        (old: any) => {
          const rows = old?.data;
          if (!Array.isArray(rows)) return old;
          let changed = false;
          const nextRows = rows.map((row: any) => {
            if (row.id !== admission.id) return row;
            changed = true;
            const total = Number(row.totalAmount) || 0;
            const paidAmount = Number(
              ((Number(row.paidAmount) || 0) + collected).toFixed(2),
            );
            const dueAmount = Number(
              Math.max(0, (Number(row.dueAmount) || 0) - collected).toFixed(2),
            );
            return {
              ...row,
              paidAmount: total > 0 ? Math.min(total, paidAmount) : paidAmount,
              dueAmount,
              // Settled invoices are not a finished bill while charges remain
              // unbilled (the backend reports PARTIAL for those).
              paymentStatus:
                dueAmount > 0
                  ? 'DUE'
                  : Number(row.unbilledCount || 0) > 0
                    ? 'PARTIAL'
                    : 'PAID',
            };
          });
          return changed ? { ...old, data: nextRows } : old;
        },
      );

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admissions'] }),
        queryClient.invalidateQueries({
          queryKey: ['admission-billing-status', admission.id],
        }),
      ]);

      toast.success(
        `Paid ${money(collected)} across ${payments.length} invoice${
          payments.length === 1 ? '' : 's'
        }`,
      );
      setPayOpen(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to record the payment. Please try again.');
    }
  };

  /**
   * Writes the admission's whole outstanding balance off to the Risk Fund.
   * The user must give a reason (stored on the entry for audit); the backend
   * clears every unpaid invoice on the visit and records a single ADMISSION
   * entry, so the list row stops reporting a due.
   */
  const handleMoveToRiskFund = async () => {
    const reason = riskFundReason.trim();
    if (!reason) {
      setRiskFundError('Enter a reason for moving the balance to the Risk Fund.');
      return;
    }
    const maxAmount = Number(totals.due.toFixed(2));
    const amount = Number(riskFundAmount);
    if (!Number.isFinite(amount) || amount <= 0 || amount > maxAmount + 0.001) {
      setRiskFundError(
        `Enter an amount between ৳0.01 and ৳${maxAmount.toLocaleString()}.`,
      );
      return;
    }
    setRiskFundError(null);
    try {
      const res: any = await moveToRiskFund.mutateAsync({
        admissionIds: [admission.id],
        reason,
        amount: Number(amount.toFixed(2)),
      });
      const movedCount = res?.data?.moved?.length ?? 0;
      const skipped = res?.data?.skipped ?? [];
      const totalAmount = Number(res?.data?.totalAmount) || 0;

      if (movedCount === 0) {
        setRiskFundError(
          skipped[0]?.reason ||
            'This admission had no outstanding invoice to move.',
        );
        return;
      }

      toast.success(
        `Moved ${money(totalAmount)} due to the Risk Fund for ${patientName}${
          patient.registrationNumber ? ` (${patient.registrationNumber})` : ''
        }`,
      );
      if (skipped.length > 0) {
        toast.warning(skipped[0]?.reason);
      }
      setRiskFundOpen(false);
      setRiskFundReason('');
      setRiskFundAmount('');
      onClose();
    } catch (err: any) {
      setRiskFundError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to move the balance to the Risk Fund',
      );
    }
  };

  // The receipt is rendered as a server-side PDF: that keeps the browser's own
  // print header/footer (date, page title, URL) off every page, and lets the
  // configured invoice/document printer receive it directly.
  const handlePrintReceipt = async () => {
    // A cancelled admission prints even with nothing invoiced: the paper is the
    // record of the cancelled stay (status, timeline, zero balance).
    if (invoices.length === 0 && !cancelled) {
      toast.error('No receipt available for this admission yet.');
      return;
    }
    setPrinting(true);
    try {
      const url = await ipdService.fetchAdmissionReceiptPdfUrl(admission.id);
      await printInvoicePdf(url, {
        onError: (message) => toast.error(message),
      });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to print the receipt.');
    } finally {
      setPrinting(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-3 backdrop-blur-md sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Admission details"
        onClick={onClose}
      >
        <div
          className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-border bg-surface shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* ===== HERO HEADER ===== */}
          <div className="relative shrink-0 overflow-hidden border-b border-border">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent" />
            <div className="absolute -right-12 -top-20 h-44 w-44 rounded-full bg-primary/15 blur-3xl" />
            <div className="absolute -left-16 bottom-0 h-32 w-32 rounded-full bg-primary-light/10 blur-2xl" />

            <div className="relative px-5 pb-4 pt-5 sm:px-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-3.5">
                  <div className="relative shrink-0">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary-light text-lg font-bold text-white shadow-lg shadow-primary/30">
                      {initials || <Stethoscope size={22} />}
                    </div>
                    <span
                      className={`absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-surface shadow ${
                        cancelled ? 'bg-danger' : 'bg-success'
                      }`}
                    >
                      {cancelled ? (
                        <Ban size={11} className="text-white" />
                      ) : (
                        <CheckCircle2 size={11} className="text-white" />
                      )}
                    </span>
                  </div>

                  <div className="min-w-0">
                    <h3 className="truncate text-lg font-bold leading-tight tracking-tight text-text">
                      {patientName}
                    </h3>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      {patient.registrationNumber && (
                        <MetaChip>
                          <span className="font-mono">#{patient.registrationNumber}</span>
                        </MetaChip>
                      )}
                      {(patient.gender || patient.age) && (
                        <MetaChip icon={UserRound}>
                          {[patient.gender, patient.age ? `${patient.age} yrs` : null]
                            .filter(Boolean)
                            .join(' · ')}
                        </MetaChip>
                      )}
                      {patient.mobileNumber && (
                        <MetaChip icon={Phone}>{patient.mobileNumber}</MetaChip>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                          cancelled
                            ? 'bg-danger/10 text-danger'
                            : 'bg-success/10 text-success'
                        }`}
                      >
                        {cancelled ? <Ban size={10} /> : <CheckCircle2 size={10} />}
                        {statusLabel}
                      </span>
                      {isEmergency && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 ring-1 ring-amber-300/70"
                          title="Emergency admission — registered without a bed (a bed can be assigned later)"
                        >
                          <AlertTriangle size={10} />
                          Emergency
                        </span>
                      )}
                      {admission.dischargeType && (
                        <span className="inline-flex items-center rounded-full border border-border bg-surface/80 px-2 py-0.5 text-[10px] font-semibold text-text-muted">
                          {titleCase(admission.dischargeType)}
                        </span>
                      )}
                      <AdmissionPaymentBadge
                        status={paymentState}
                        size="md"
                        detail={
                          paymentState === 'DUE'
                            ? money(totals.due)
                            : paymentState === 'RISK_FUND' && writtenOffAmount > 0
                              ? `${money(writtenOffAmount)} written off`
                              : undefined
                        }
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={onClose}
                  className="shrink-0 rounded-xl border border-border/70 bg-surface/80 p-2 text-text-muted backdrop-blur transition-colors hover:bg-surface hover:text-text"
                  title="Close (Esc)"
                  aria-label="Close details"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* ===== BODY ===== */}
          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4 sm:px-6">
            {/* Stay timeline */}
            <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-text-muted">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    Admitted
                    {canEditAdmissionTime && (
                      <InfoHint
                        label="Admission Date & Time"
                        en="Correcting the admission date (backdating) also re-dates this stay's live invoices and their ledger entries. Requires the “Add Backdate Admission” permission."
                        bn="ভর্তির তারিখ সংশোধন (ব্যাকডেট) করলে এই স্টের সক্রিয় ইনভয়েস ও লেজার এন্ট্রিও নতুন তারিখে বসে। “Add Backdate Admission” পারমিশন প্রয়োজন।"
                      />
                    )}
                  </p>
                  <p className="mt-1 text-xs font-semibold leading-snug text-text">
                    {formatOrdinalDateTimeLong(admittedAt)}
                  </p>

                  {canEditAdmissionTime && (
                    <div className="mt-2 max-w-[15rem] space-y-2">
                      <DateTimePicker
                        value={admittedAtValue}
                        onChange={(value) => {
                          setAdmittedAtValue(value);
                          if (value) setAdmissionDateError(null);
                        }}
                        timeZone={hospitalTimezone}
                        error={admissionDateError || undefined}
                        required
                      />
                      {admissionDateChanged && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handleSaveAdmissionDate}
                            disabled={updateAdmission.isPending}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
                          >
                            {updateAdmission.isPending ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <CheckCircle2 size={12} />
                            )}
                            Save date
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setAdmittedAtValue(savedAdmittedAtLocal);
                              setAdmissionDateError(null);
                            }}
                            disabled={updateAdmission.isPending}
                            className="rounded-lg border border-border px-2.5 py-1 text-[11px] font-medium text-text-muted transition-colors hover:text-text disabled:opacity-50"
                          >
                            Revert
                          </button>
                        </div>
                      )}
                      <p className="text-[9px] leading-3 text-text-muted">
                        Times in {hospitalTimezone} (Settings → General)
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex w-24 shrink-0 flex-col items-center gap-1 self-center sm:w-32">
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
                    {stayNights ? `${stayNights} night${stayNights === 1 ? '' : 's'}` : 'Stay'}
                  </span>
                  <div
                    className={`relative h-px w-full bg-gradient-to-r from-primary/50 via-primary/25 ${
                      cancelled ? 'to-danger/60' : 'to-success/60'
                    }`}
                  >
                    <span className="absolute -left-0.5 -top-[3px] h-[7px] w-[7px] rounded-full bg-primary" />
                    <span
                      className={`absolute -right-0.5 -top-[3px] h-[7px] w-[7px] rounded-full ${
                        cancelled ? 'bg-danger' : 'bg-success'
                      }`}
                    />
                  </div>
                  <span className="text-[9px] uppercase tracking-wide text-text-muted">
                    length of stay
                  </span>
                </div>

                <div className="min-w-0 flex-1 text-right">
                  <p className="flex items-center justify-end gap-1.5 text-[10px] font-bold uppercase tracking-wider text-text-muted">
                    {cancelled ? 'Cancelled' : 'Discharged'}
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        cancelled ? 'bg-danger' : 'bg-success'
                      }`}
                    />
                  </p>
                  <p className="mt-1 text-xs font-semibold leading-snug text-text">
                    {formatOrdinalDateTimeLong(closedAt)}
                  </p>
                </div>
              </div>

              {cancelled && (
                <div className="mt-3 flex items-start gap-2 rounded-xl border border-danger/30 bg-danger/5 px-3 py-2">
                  <Ban size={12} className="mt-0.5 shrink-0 text-danger" />
                  <p className="text-[10px] leading-relaxed text-text-muted">
                    <span className="font-bold uppercase tracking-wide text-danger">
                      Cancelled
                    </span>{' '}
                    — this stay was cancelled before discharge. The bed was released and no
                    further charges can be added.
                  </p>
                </div>
              )}
            </div>

            {/* Admission facts */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <InfoTile
                icon={BedDouble}
                label="Bed / Ward"
                value={bedLabel}
                hint={
                  Number(bed?.price) > 0
                    ? `৳${Number(bed.price).toLocaleString()}/day`
                    : undefined
                }
              />
              <InfoTile
                icon={Stethoscope}
                label="Doctor"
                value={admission.doctor?.name || 'Unknown'}
                hint={admission.doctor?.designation?.name || undefined}
              />
              <InfoTile
                icon={ClipboardList}
                label="Admission type"
                value={titleCase(admission.admissionType)}
              />
              {(referredByName || referredFrom) && (
                <InfoTile
                  icon={Stethoscope}
                  label={referredByName ? 'Referred by' : 'Referred from'}
                  value={referredByName || referredFrom}
                  hint={
                    referredByName && referredFrom ? `Referred from ${referredFrom}` : undefined
                  }
                />
              )}
              <InfoTile
                icon={Receipt}
                label="Bills raised"
                value={`${invoices.length} invoice${invoices.length === 1 ? '' : 's'}`}
                hint={
                  invoices.length > 0
                    ? paymentState === 'RISK_FUND'
                      ? `${money(writtenOffAmount)} written off`
                      : `${money(totals.paid)} collected`
                    : 'Nothing invoiced for this visit'
                }
              />
            </div>

            {/* Referral commission — only when a partner is actually attached */}
            {hasReferralPartner && (
              <div className="rounded-2xl border border-border bg-surface px-4 py-3 shadow-sm">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <UserPlus size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
                      Referral commission
                    </p>
                    <p
                      className="truncate text-xs font-semibold text-text"
                      title={referralName || 'Referral partner'}
                    >
                      {referralName || 'Referral partner'}
                    </p>
                    <p className="truncate text-[10px] text-text-muted" title={referralDetail}>
                      {[REFERRAL_TYPE_LABEL[referral?.type || ''] || null, referralDetail]
                        .filter(Boolean)
                        .join(' · ') || 'Referral partner'}
                    </p>
                  </div>
                  {referralFlatAmount > 0 && (
                    <div className="shrink-0 text-right">
                      <p className="text-xs font-bold text-primary">
                        {money(referralFlatAmount)}
                      </p>
                      <p className="text-[9px] uppercase tracking-wide text-text-muted">
                        commission
                      </p>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-[10px] leading-relaxed text-text-muted">
                  {referralFlatAmount > 0
                    ? 'A flat commission paid to this referral partner once the admission bill is fully settled. It does not change what the patient pays.'
                    : 'A referral partner is linked to this admission, but no commission amount was set.'}
                </p>
              </div>
            )}

            {/* Billing summary */}
            <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
              <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-text-muted">
                    <Wallet size={13} className="text-primary" />
                    Billing
                  </span>
                  <AdmissionPaymentBadge status={paymentState} size="sm" />
                </div>
                <span className="flex items-center gap-1.5 text-[10px] text-text-muted">
                  {billingLoading && <Loader2 size={11} className="animate-spin" />}
                  {invoices.length} invoice{invoices.length === 1 ? '' : 's'}
                </span>
              </div>

              <div className="space-y-3 p-4">
                {invoices.length > 0 ? (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      <AmountTile
                        label="Total"
                        value={totals.total}
                        tone="text-text"
                        tint="border-border bg-background/60"
                      />
                      <AmountTile
                        label="Paid"
                        value={totals.paid}
                        tone="text-success"
                        tint="border-success/20 bg-success/5"
                      />
                      <AmountTile
                        label="Due"
                        value={totals.due}
                        tone="text-danger"
                        tint="border-danger/20 bg-danger/5"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between text-[10px] text-text-muted">
                        <span>{paidPercent}% of the bill paid</span>
                        <span>
                          {paymentState === 'RISK_FUND'
                            ? `${money(writtenOffAmount)} written off to the Risk Fund`
                            : `${money(Math.max(0, totals.total - totals.paid))} remaining`}
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-border/70">
                        <div
                          className={`h-full rounded-full transition-all ${
                            paymentState === 'RISK_FUND'
                              ? 'bg-gradient-to-r from-warning/80 to-warning'
                              : 'bg-gradient-to-r from-success/80 to-success'
                          }`}
                          style={{ width: `${paidPercent}%` }}
                        />
                      </div>
                    </div>

                    <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
                      {invoices.map((inv) => {
                        const due = Number(inv.dueAmount) || 0;
                        // A written-off invoice has no due left, so it must not
                        // fall through to the "Paid" pill.
                        const isRiskFundInvoice = isRiskFundInvoiceStatus(inv.status);
                        const writtenOff = Math.max(
                          0,
                          (Number(inv.total) || 0) - (Number(inv.paidAmount) || 0),
                        );
                        return (
                          <div
                            key={inv.id}
                            className="flex items-center justify-between gap-3 bg-surface px-3 py-2"
                          >
                            <div className="min-w-0">
                              <p className="truncate font-mono text-[11px] font-semibold text-text">
                                {inv.invoiceNumber}
                              </p>
                              <p className="text-[10px] text-text-muted">
                                {formatOrdinalDateTimeLong(inv.createdAt)}
                              </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <div className="text-right">
                                <p className="text-xs font-bold text-text">
                                  {money(Number(inv.total) || 0)}
                                </p>
                                {due > 0 && (
                                  <p className="text-[10px] font-medium text-danger">
                                    {money(due)} due
                                  </p>
                                )}
                                {isRiskFundInvoice && writtenOff > 0 && (
                                  <p className="text-[10px] font-semibold text-warning">
                                    {money(writtenOff)} written off
                                  </p>
                                )}
                              </div>
                              <span
                                className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                                  isRiskFundInvoice
                                    ? 'bg-warning text-white ring-1 ring-warning/40'
                                    : due > 0
                                      ? 'bg-danger/10 text-danger'
                                      : 'bg-success/10 text-success'
                                }`}
                              >
                                {isRiskFundInvoice ? 'Risk Fund' : due > 0 ? 'Due' : 'Paid'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {paymentState === 'RISK_FUND' && (
                      <div className="flex items-start gap-2 rounded-xl border border-warning/30 bg-warning/5 px-3 py-2">
                        <ShieldAlert size={13} className="mt-0.5 shrink-0 text-warning" />
                        <p className="text-[10px] leading-relaxed text-text-muted">
                          <span className="font-bold uppercase tracking-wide text-warning">
                            Risk Fund
                          </span>{' '}
                          — the outstanding balance was written off
                          {cancelled
                            ? ' when this admission was cancelled'
                            : ''}, so nothing is left to collect from the patient.
                        </p>
                      </div>
                    )}

                    {/* Legacy / failed write-off: a cancelled stay must never keep a
                        due, so point staff at the action that clears it. */}
                    {cancelled && paymentState === 'DUE' && totals.due > 0 && (
                      <div className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger/5 px-3 py-2">
                        <Ban size={13} className="mt-0.5 shrink-0 text-danger" />
                        <p className="text-[10px] leading-relaxed text-text-muted">
                          <span className="font-bold uppercase tracking-wide text-danger">
                            Unwritten balance
                          </span>{' '}
                          — this cancelled stay still shows {money(totals.due)} unpaid. A
                          cancelled visit can never be collected, so move it to the Risk Fund.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-background/40 px-3.5 py-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-500/10 text-text-muted">
                      <Receipt size={16} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-text">No invoice raised</p>
                      <p className="text-[11px] text-text-muted">
                        {Number(billing?.unbilledAmount || 0) > 0
                          ? `${money(Number(billing?.unbilledAmount))} in unbilled charges (${billing?.unbilledCount} item${
                              Number(billing?.unbilledCount) === 1 ? '' : 's'
                            })`
                          : cancelled
                            ? 'This visit was cancelled without billing.'
                            : 'This visit was discharged without billing.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Clinical notes */}
            {(diagnosis !== 'N/A' ||
              admission.dischargeSummary ||
              admission.dischargeInstructions ||
              ((admission.dischargeDueNote || admission.dischargeDueDate) && totals.due > 0)) && (
              <div className="space-y-2">
                {diagnosis !== 'N/A' && (
                  <NoteCard icon={FileText} title="Diagnosis / admission reason">
                    <p className="whitespace-pre-line text-xs leading-relaxed text-text">
                      {diagnosis}
                    </p>
                  </NoteCard>
                )}

                {admission.dischargeSummary && (
                  <NoteCard icon={ClipboardList} title="Discharge summary">
                    <p className="whitespace-pre-line text-xs leading-relaxed text-text">
                      {admission.dischargeSummary}
                    </p>
                  </NoteCard>
                )}

                {admission.dischargeInstructions && (
                  <NoteCard icon={Stethoscope} title="Discharge instructions">
                    <p className="whitespace-pre-line text-xs leading-relaxed text-text">
                      {admission.dischargeInstructions}
                    </p>
                  </NoteCard>
                )}

                {(admission.dischargeDueNote || admission.dischargeDueDate) && totals.due > 0 && (
                  <div className="rounded-xl border border-warning/30 bg-warning/5 px-3.5 py-3">
                    <div className="mb-1 flex items-center gap-1.5">
                      <AlertCircle size={12} className="text-warning" />
                      <p className="text-[10px] font-bold uppercase tracking-wider text-warning">
                        Carried balance
                      </p>
                    </div>
                    {admission.dischargeDueNote && (
                      <p className="text-xs leading-relaxed text-text">
                        {admission.dischargeDueNote}
                      </p>
                    )}
                    {admission.dischargeDueDate && (
                      <p className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 text-[10px] font-medium text-text-muted">
                        <Calendar size={10} />
                        Promised on {formatOrdinalDateTimeLong(admission.dischargeDueDate)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ===== FOOTER ===== */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-background/60 px-5 py-3.5 sm:px-6">
            <div className="flex flex-wrap items-center gap-2">
              {canPay && totals.due > 0 && (
                <button
                  onClick={openPayment}
                  className="flex items-center gap-2 rounded-xl bg-success px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-success/90"
                  title="Record a payment against the outstanding due"
                >
                  <Banknote size={15} />
                  Pay Due
                </button>
              )}
              {canManageRiskFund && totals.due > 0 && (
                <button
                  onClick={() => {
                    setRiskFundError(null);
                    setRiskFundReason('');
                    setRiskFundAmount(String(Number(totals.due.toFixed(2))));
                    setRiskFundOpen(true);
                  }}
                  className="flex items-center gap-2 rounded-xl bg-warning px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-warning/90"
                  title={
                    cancelled
                      ? 'A cancelled admission can never collect its due — write it off to the Risk Fund'
                      : 'Move the outstanding due to the Risk Fund'
                  }
                >
                  <AlertTriangle size={15} />
                  Move to Risk Fund
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handlePrintReceipt}
                disabled={printing || (invoices.length === 0 && !cancelled)}
                className="flex items-center gap-1.5 rounded-xl border border-border px-3.5 py-2 text-sm font-medium text-text transition-colors hover:bg-background disabled:opacity-50"
                title={
                  cancelled
                    ? 'Print the cancelled admission receipt'
                    : 'Print the invoice receipt'
                }
              >
                {printing ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Printer size={14} />
                )}
                Print
              </button>
              <button
                onClick={onClose}
                className="rounded-xl border border-border px-3.5 py-2 text-sm font-medium text-text-muted transition-colors hover:bg-background hover:text-text"
              >
                Close
              </button>
              <button
                onClick={() => onViewAdmission(admission.id)}
                className="flex items-center gap-1.5 rounded-xl bg-primary/10 px-3.5 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/15"
              >
                Open Admission
                <ArrowUpRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ===== MOVE TO RISK FUND CONFIRMATION ===== */}
      <RiskFundMoveDialog
        open={riskFundOpen}
        title="Move admission due to Risk Fund"
        description={
          <>
            Move <strong>{money(Number(riskFundAmount) || 0)}</strong> of the{' '}
            <strong>{money(totals.due)}</strong> outstanding balance for{' '}
            <strong>{patientName}</strong> to the Risk Fund?{' '}
            {Number(riskFundAmount) >= Number(totals.due.toFixed(2)) - 0.004
              ? 'Every unpaid invoice on this admission will be written off.'
              : 'The rest stays collectible on the admission.'}
          </>
        }
        reason={riskFundReason}
        onReasonChange={setRiskFundReason}
        maxAmount={Number(totals.due.toFixed(2))}
        amount={riskFundAmount}
        onAmountChange={setRiskFundAmount}
        error={riskFundError}
        loading={moveToRiskFund.isPending}
        confirmLabel={`Move ৳${(Number(riskFundAmount) || 0).toLocaleString()} to Risk Fund`}
        onConfirm={handleMoveToRiskFund}
        onCancel={() => {
          if (moveToRiskFund.isPending) return;
          setRiskFundOpen(false);
          setRiskFundReason('');
          setRiskFundAmount('');
          setRiskFundError(null);
        }}
      />

      {/* ===== DUE PAYMENT MODAL ===== */}
      {payOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Pay due payment"
          onClick={closePayment}
        >
          <div
            className="bg-surface rounded-2xl border border-border w-full max-w-md max-h-[90vh] flex flex-col shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border bg-background/40">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-success/10 flex items-center justify-center shrink-0">
                  <Banknote size={18} className="text-success" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-text leading-tight">Due Payment</h3>
                  <p className="text-[11px] text-text-muted truncate">{patientName}</p>
                </div>
              </div>
              <button
                onClick={closePayment}
                disabled={bulkRecordPayments.isPending}
                className="p-1.5 rounded-lg text-text-muted hover:text-text hover:bg-background transition-colors shrink-0 disabled:opacity-50"
                title="Close"
                aria-label="Close due payment"
              >
                <X size={18} />
              </button>
            </div>

            <div className="overflow-y-auto px-5 py-4 space-y-3.5">
              {/* Invoice selection — tick one, several, or all */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wide text-text-muted">
                    Invoices
                    <span className="ml-1.5 normal-case font-medium">
                      ({selectedInvoices.length}/{dueInvoices.length} selected)
                    </span>
                  </label>
                  {dueInvoices.length > 1 && (
                    <button
                      type="button"
                      onClick={toggleAll}
                      className="text-[11px] font-semibold text-primary hover:underline"
                    >
                      {allSelected ? 'Clear all' : 'All invoices'}
                    </button>
                  )}
                </div>
                <div className="rounded-xl border border-border divide-y divide-border overflow-hidden max-h-44 overflow-y-auto bg-surface">
                  {dueInvoices.map((inv) => {
                    const checked = selectedIds.includes(inv.id);
                    return (
                      <label
                        key={inv.id}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                          checked ? 'bg-primary/5' : 'hover:bg-background/60'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleInvoice(inv.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-mono font-medium text-text truncate">
                            {inv.invoiceNumber}
                          </p>
                          <p className="text-[10px] text-text-muted">
                            {new Date(inv.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xs font-semibold text-danger">
                            {money(Number(inv.dueAmount) || 0)}
                          </p>
                          <p className="text-[10px] text-text-muted">
                            of {money(Number(inv.total) || 0)}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                  {dueInvoices.length === 0 && (
                    <p className="px-3 py-4 text-center text-xs text-text-muted">
                      No invoices with an outstanding balance
                    </p>
                  )}
                </div>
              </div>

              {/* Balance summary */}
              <div className="rounded-xl border border-border bg-background/40 p-3.5 space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-text-muted">Bill total</span>
                  <span className="text-text font-medium">{money(totals.total)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-text-muted">Paid so far</span>
                  <span className="text-success font-medium">{money(totals.paid)}</span>
                </div>
                <div className="flex items-center justify-between text-xs pt-1.5 border-t border-border">
                  <span className="text-text-muted">Outstanding due</span>
                  <span className="text-danger font-semibold">{money(totals.due)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-text-muted">
                    Selected to pay ({selectedInvoices.length})
                  </span>
                  <span className="text-primary font-semibold">{money(selectedDue)}</span>
                </div>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wide text-text-muted mb-1">
                  Amount Received
                </label>
                <ValidatedNumberInput
                  value={amount}
                  onChange={(value) => {
                    setAmountDirty(true);
                    setAmount(value);
                  }}
                  max={selectedDue}
                  placeholder="0.00"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
                <div className="flex items-center justify-between mt-1.5">
                  <p className="text-[11px] text-text-muted">
                    Selected due {money(selectedDue)}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setAmountDirty(false);
                      setAmount(selectedDue);
                    }}
                    disabled={selectedDue <= 0}
                    className="text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
                  >
                    Pay full due
                  </button>
                </div>
              </div>

              {/* Mode */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wide text-text-muted mb-1">
                  Payment Mode
                </label>
                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date received — drives the cash-collected date. */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wide text-text-muted mb-1">
                  Payment Date
                </label>
                <input
                  type="datetime-local"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <p className="text-[11px] text-text-muted mt-1">
                  The collected amount is posted on this date.
                </p>
              </div>

              {/* Reference */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wide text-text-muted mb-1">
                  Reference <span className="normal-case font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="e.g. txn id, cheque no."
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              <div className="flex items-center justify-between text-xs pt-1.5 border-t border-border">
                <span className="text-text font-medium">Remaining due on selection</span>
                <span className="text-text font-semibold">{money(remainingDue)}</span>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-2.5 bg-danger/10 border border-danger/20 rounded-lg text-xs text-danger">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-border bg-background/40">
              <button
                onClick={closePayment}
                disabled={bulkRecordPayments.isPending}
                className="px-3.5 py-2 rounded-lg border border-border text-text-muted hover:text-text hover:bg-background transition-colors text-sm font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePay}
                disabled={
                  bulkRecordPayments.isPending ||
                  selectedInvoices.length === 0 ||
                  Number(amount) <= 0
                }
                className="px-3.5 py-2 rounded-lg bg-success text-white hover:bg-success/90 transition-colors text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                {bulkRecordPayments.isPending ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Banknote size={15} />
                )}
                Record Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
